import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { complianceService } from '../../../services/adminService';

interface Props {
  programId: string;
}

interface RetentionPayload {
  durations: Record<string, number>; // days
}

const RetentionPolicy: React.FC<Props> = ({ programId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<RetentionPayload>({ durations: {} });

  useEffect(() => {
    const run = async () => {
      if (!programId) return;
      setLoading(true);
      try {
        const res = await complianceService.getRetentionPolicy(programId);
        setData({ durations: res?.durations ?? {} });
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load retention policy');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [programId]);

  const updateKey = (key: string, val: number) => {
    setData((prev) => ({ ...prev, durations: { ...prev.durations, [key]: val } }));
  };

  const addCategory = () => {
    const key = prompt('Enter data category key (e.g., pii):');
    if (!key) return;
    updateKey(key, 365);
  };

  const save = async () => {
    if (!programId) {
      toast.error('Program ID required');
      return;
    }
    if (Object.values(data.durations).some((d) => d < 0)) {
      toast.error('Durations must be non-negative');
      return;
    }
    setSaving(true);
    try {
      await complianceService.updateRetentionPolicy(programId, data);
      toast.success('Retention policy saved');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-medium">Data Retention</h2>
        <div className="flex gap-2">
          <button onClick={addCategory} className="px-3 py-2 text-sm rounded border">Add Category</button>
          <button onClick={save} disabled={saving || !programId} className="px-3 py-2 text-sm rounded bg-indigo-600 text-white disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
      {!programId && <div className="text-sm text-gray-500">Enter a Program ID to load policy.</div>}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="border rounded divide-y">
          {Object.keys(data.durations).length === 0 && (
            <div className="p-4 text-sm text-gray-500">No categories configured. Click "Add Category" to begin.</div>
          )}
          {Object.entries(data.durations).map(([key, val]) => (
            <div key={key} className="grid grid-cols-2 gap-4 p-4 items-center">
              <div>
                <div className="font-medium">{key}</div>
                <div className="text-xs text-gray-500">Days to retain</div>
              </div>
              <input
                type="number"
                className="border rounded px-3 py-2 w-40"
                value={val}
                onChange={(e) => updateKey(key, Number(e.target.value))}
                min={0}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RetentionPolicy;
